import React from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../../routes/routes";

interface GetDisabledNoteProps {
  needsSwimmer: boolean;
  doesntNeedSwimmer: boolean;
  reportTypeName: string; // e.g., "novedad", "evento adverso"
  gender?: "masculine" | "feminine";
}

/**
 * getDisabledNote
 * Helper function to generate disabled note JSX for form radio options.
 * Returns appropriate message based on whether swimmer is needed or not.
 *
 * @param needsSwimmer - Whether the option requires a swimmer
 * @param doesntNeedSwimmer - Whether the option doesn't need a swimmer
 * @param reportTypeName - Name of the report type (for message context)
 * @param gender - Gender of the report type name for proper Spanish grammar
 * @returns JSX element with the disabled note message, or undefined if not disabled
 */
export const getDisabledNote = ({
  needsSwimmer,
  doesntNeedSwimmer,
  reportTypeName,
  gender = "feminine",
}: GetDisabledNoteProps): React.ReactNode => {
  if (!needsSwimmer && !doesntNeedSwimmer) {
    return undefined;
  }

  // Determine the correct article and verb form based on gender
  const article = gender === "masculine" ? "este" : "esta";
  const verb = gender === "masculine" ? "asociarlo" : "asociarla";
  const createVerb = gender === "masculine" ? "crearlo" : "crearla";

  if (needsSwimmer) {
    // Needs swimmer: direct to swimmers table
    return (
      <>
        Para crear {article} tipo de {reportTypeName} es necesario {verb} a un
        bañista, para ello debe ir a la{" "}
        <Link
          to={ROUTES.DASHBOARD.LIFEGUARD}
          className="text-blue-600 hover:text-blue-800 underline font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          tabla de bañistas
        </Link>
        .
      </>
    );
  }

  if (doesntNeedSwimmer) {
    // Doesn't need swimmer: direct to register without swimmer
    return (
      <>
        Para crear {article} tipo de {reportTypeName} debe {createVerb} desde
        este{" "}
        <Link
          to={ROUTES.NEWS.REGISTER}
          className="text-blue-600 hover:text-blue-800 underline font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          sitio
        </Link>
        .
      </>
    );
  }

  return undefined;
};

